import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, LogOut } from "lucide-react";
import { toast } from "sonner";

const homeFor = (role) =>
  role === "courier" ? "/livreur" : role === "logistics_manager" ? "/orders" : "/dashboard";

export default function ForcePasswordChange() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    if (pw !== confirm) {
      setError("Les deux mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/set-password", { new_password: pw });
      setUser(data);
      toast.success("Mot de passe mis à jour avec succès");
      navigate(homeFor(data.role), { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Échec de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  const forced = user && user.must_change_password;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-sm animate-fade-in" data-testid="set-password-page">
        <div className="flex justify-center mb-6">
          <Logo size={44} />
        </div>
        <div className="bg-white border rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 text-brand-700 mb-1">
            <KeyRound className="w-5 h-5" />
            <h2 className="text-xl font-bold font-heading text-slate-900">Nouveau mot de passe</h2>
          </div>
          <p className="text-sm text-slate-500 mb-5">
            {forced
              ? "Pour votre sécurité, veuillez définir un nouveau mot de passe avant de continuer."
              : "Définissez un nouveau mot de passe pour votre compte."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="new-pw">Nouveau mot de passe</Label>
              <Input
                id="new-pw"
                data-testid="set-password-new"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Au moins 8 caractères"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="confirm-pw">Confirmer le nouveau mot de passe</Label>
              <Input
                id="confirm-pw"
                data-testid="set-password-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Retapez le mot de passe"
                required
                className="mt-1.5"
              />
            </div>
            {error && (
              <p data-testid="set-password-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            <Button
              data-testid="set-password-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-brand-600 hover:bg-brand-700 h-11"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer et continuer"}
            </Button>
          </form>
        </div>

        <button
          data-testid="set-password-logout"
          onClick={() => { logout(); navigate("/login", { replace: true }); }}
          className="mt-4 mx-auto flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600"
        >
          <LogOut className="w-3.5 h-3.5" /> Se déconnecter
        </button>
      </div>
    </div>
  );
}
